import clsx from "clsx";

import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import { TextField } from "../base/TextField";
import { IInitialPriceEstimateCategorySummary } from "../../models/Category/InitialPriceEstimate";
import { serpPlaygroundUrl } from "../../models/SerpQuery/SerpQuery";

interface CategoryListProps {
  categories: IInitialPriceEstimateCategorySummary[];
  selectedId: string | undefined;
  searchTerm: string;
  isLoading: boolean;
  error: Error | null;
  onSearchChange: (value: string) => void;
  onSelect: (partCategoryId: string) => void;
  onAdd: () => void;
  onRemove: (category: IInitialPriceEstimateCategorySummary) => void;
}

/** The priced categories, in the order a lead prices them. */
export default function CategoryList({
  categories,
  selectedId,
  searchTerm,
  isLoading,
  error,
  onSearchChange,
  onSelect,
  onAdd,
  onRemove,
}: CategoryListProps) {
  const isSearching = searchTerm.trim().length > 0;

  return (
    <div className="bg-white rounded shadow-sm border border-gray-200 flex flex-col max-h-[calc(100vh-12rem)]">
      <div className="p-3 border-b border-gray-100">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
            Priced Categories
          </h2>
          <Button size="xs" onClick={onAdd}>
            <span className="flex items-center gap-1">
              <PlusIcon className="h-3.5 w-3.5" /> Add
            </span>
          </Button>
        </div>

        <div className="relative">
          <TextField
            fullWidth
            small
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            className="!py-1.5 pr-8"
          />
          <MagnifyingGlassIcon className="pointer-events-none absolute right-2 top-2 h-4 w-4 text-gray-400" />
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {isLoading && (
          <p className="px-3 py-4 text-sm text-gray-500">Loading categories…</p>
        )}

        {/* Never claim the list is empty when the request failed. */}
        {!isLoading && error && (
          <div className="m-3 rounded border border-red-200 bg-red-50 p-3">
            <p className="text-sm font-medium text-red-800">
              Could not load categories
            </p>
            <p className="mt-1 text-xs text-red-700">{error.message}</p>
          </div>
        )}

        {!isLoading && !error && categories.length === 0 && (
          <div className="px-3 py-6 text-center">
            <p className="text-sm text-gray-600">
              {isSearching
                ? "No priced categories match that search."
                : "No categories are priced yet."}
            </p>
            {!isSearching && (
              <>
                <p className="mt-1 text-xs text-gray-500">
                  Leads and quotes get no initial price estimate until you add
                  a category here.
                </p>
                <Button size="sm" className="mt-3" onClick={onAdd}>
                  Add a category
                </Button>
              </>
            )}
          </div>
        )}

        <ul className="divide-y divide-gray-100">
          {categories.map((category) => (
            <li
              key={category.partCategoryId}
              className={clsx(
                "group flex items-center",
                selectedId === category.partCategoryId
                  ? "bg-orange-50"
                  : "hover:bg-gray-50",
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(category.partCategoryId)}
                className={clsx(
                  "flex flex-1 items-center justify-between gap-2 px-3 py-2 text-left text-sm min-w-0",
                  selectedId === category.partCategoryId && "font-medium",
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate">{category.name}</span>
                  {category.searchKeyword && (
                    <span className="block truncate text-xs font-normal text-gray-500">
                      Keyword “{category.searchKeyword}”
                    </span>
                  )}
                </span>

                {!category.isActive && (
                  <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                    Inactive
                  </span>
                )}
              </button>

              {category.exampleSerpQuery && (
                <a
                  href={serpPlaygroundUrl(category.exampleSerpQuery)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`View raw Serp for ${category.name}`}
                  title="View raw Serp: run this category's example query in the SerpApi playground"
                  className="px-1 text-gray-300 opacity-0 transition-opacity hover:text-orange-700 focus:opacity-100 group-hover:opacity-100"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}

              <button
                type="button"
                aria-label={`Remove ${category.name}`}
                title={`Remove ${category.name}`}
                onClick={() => onRemove(category)}
                className="px-2 text-gray-300 opacity-0 transition-opacity hover:text-red-600 focus:opacity-100 group-hover:opacity-100"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
        Categories not listed here are not priced when a lead or quote
        arrives.
      </p>
    </div>
  );
}
